/**
 * useWebhooksPage
 * Refatorado: Lógica dividida em useWebhookModals e useEndpointModals.
 * Este hook agora atua como orquestrador.
 */
import { useState, useCallback, useMemo } from 'react';
import { useWebhookModals } from './useWebhookModals';
import { useEndpointModals } from './useEndpointModals';

export type TabType = 'outgoing' | 'incoming';

export const useWebhooksPage = () => {
    const [activeTab, setActiveTab] = useState<TabType>('outgoing');
    const [searchTerm, setSearchTerm] = useState('');

    const webhookModals = useWebhookModals();
    const endpointModals = useEndpointModals();

    // Filtragem
    const filteredWebhooks = useMemo(() =>
        webhookModals.webhooks.filter(w =>
            w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            w.url.toLowerCase().includes(searchTerm.toLowerCase())
        ), [webhookModals.webhooks, searchTerm]);

    const filteredEndpoints = useMemo(() =>
        endpointModals.endpoints.filter(e =>
            e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            e.slug.toLowerCase().includes(searchTerm.toLowerCase())
        ), [endpointModals.endpoints, searchTerm]);

    const refresh = useCallback(async () => {
        if (activeTab === 'outgoing') {
            await webhookModals.refetch();
        } else {
            await endpointModals.refetch();
        }
    }, [activeTab, webhookModals, endpointModals]);

    return {
        // Tab
        activeTab,
        setActiveTab,
        searchTerm,
        setSearchTerm,
        loading: activeTab === 'outgoing' ? webhookModals.loading : endpointModals.loading,
        refresh,

        // Webhooks
        filteredWebhooks,
        webhookSelectedIds: webhookModals.selectedIds,
        toggleWebhookSelection: webhookModals.toggleSelection,
        toggleAllWebhooks: () => webhookModals.toggleAllSelection(filteredWebhooks),
        clearWebhookSelection: webhookModals.clearSelection,

        // Webhook modals
        isWebhookFormOpen: webhookModals.isFormOpen,
        setIsWebhookFormOpen: webhookModals.setIsFormOpen,
        webhookToEdit: webhookModals.webhookToEdit,
        isWebhookDeleteOpen: webhookModals.isDeleteOpen,
        setIsWebhookDeleteOpen: webhookModals.setIsDeleteOpen,
        webhookToDelete: webhookModals.webhookToDelete,
        isWebhookBulkDeleteOpen: webhookModals.isBulkDeleteOpen,
        setIsWebhookBulkDeleteOpen: webhookModals.setIsBulkDeleteOpen,

        // Webhook handlers
        openCreateWebhook: webhookModals.openCreate,
        openEditWebhook: webhookModals.openEdit,
        openDeleteWebhook: webhookModals.openDelete,
        saveWebhook: webhookModals.save,
        confirmDeleteWebhook: webhookModals.confirmDelete,
        bulkDeleteWebhooks: webhookModals.bulkDelete,
        duplicateWebhook: webhookModals.duplicate,

        // Endpoints
        filteredEndpoints,
        endpointSelectedIds: endpointModals.selectedIds,
        toggleEndpointSelection: endpointModals.toggleSelection,
        toggleAllEndpoints: () => endpointModals.toggleAllSelection(filteredEndpoints),
        clearEndpointSelection: endpointModals.clearSelection,
        getEndpointUrl: endpointModals.getEndpointUrl,
        generateToken: endpointModals.generateToken,
        generateSlug: endpointModals.generateSlug,

        // Endpoint modals
        isEndpointFormOpen: endpointModals.isFormOpen,
        setIsEndpointFormOpen: endpointModals.setIsFormOpen,
        endpointToEdit: endpointModals.endpointToEdit,
        isEndpointDeleteOpen: endpointModals.isDeleteOpen,
        setIsEndpointDeleteOpen: endpointModals.setIsDeleteOpen,
        endpointToDelete: endpointModals.endpointToDelete,
        isEndpointBulkDeleteOpen: endpointModals.isBulkDeleteOpen,
        setIsEndpointBulkDeleteOpen: endpointModals.setIsBulkDeleteOpen,
        isRegenerateTokenOpen: endpointModals.isRegenerateTokenOpen,
        setIsRegenerateTokenOpen: endpointModals.setIsRegenerateTokenOpen,
        endpointToRegenerate: endpointModals.endpointToRegenerate,

        // Endpoint handlers
        openCreateEndpoint: endpointModals.openCreate,
        openEditEndpoint: endpointModals.openEdit,
        openDeleteEndpoint: endpointModals.openDelete,
        openRegenerateToken: endpointModals.openRegenerateToken,
        saveEndpoint: endpointModals.save,
        confirmDeleteEndpoint: endpointModals.confirmDelete,
        bulkDeleteEndpoints: endpointModals.bulkDelete,
        confirmRegenerateToken: endpointModals.confirmRegenerateToken,
    };
};

